import { useLoader } from "@/app/hooks/useLoader";
import { Dialog, Menu, Transition } from "@headlessui/react";
import LabReportFilter from "../LabReportFilterComponent";
import LabReportStats from "../LabReportStatsComponent";
import { useCallback, useEffect, useMemo, useState, Fragment, useRef } from "react";
import SamplePickupRequestModal from "../SamplePickupRequestModal";
import UploadLabReportModal from "../UploadLabReportModal";
import TrfGenerateModal from "../TrfGenerateModal/index";
import EditLabPartnerModal from "../EditLabPartnerModal";
import EscalateReportModal from "../EscalateReportModal";
import { HiOutlineExternalLink } from "react-icons/hi";
import { useLocation, useNavigate } from "react-router";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { ILabReportListState } from "@/app_redux/reducers/slices/auth/lab_report_slice";
import { FiEdit, FiMoreVertical } from "react-icons/fi";
import { useUtils } from "@/app/hooks/useUtils";
import TableSkeletonComponent from "@/app/components/ui/LoaderComponent";
import { Search } from "lucide-react";
import LabReportHeader from "../LabReportHeader";
import { useLabReportFilterListAPI } from "@/app/hooks/api_hooks/LabReport/useLabReportFilterListAPI";
import { useLabReportStatsListAPI } from "@/app/hooks/api_hooks/LabReport/useLabReportStatsListAPI";
import { useRetestReportAPI } from "@/app/hooks/api_hooks/LabReport/useRetestReportAPI";
import { useDeleteTRFAPI } from "@/app/hooks/api_hooks/LabReport/useDeleteTRFAPI";
import { useRetestReasonsAPI } from "@/app/hooks/api_hooks/LabReport/useRetestReasonsAPI";
import { useDeleteTRFReasonsAPI } from "@/app/hooks/api_hooks/LabReport/useDeleteTRFReasonsAPI";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { setLabReportListFilter } from "@/app_redux/reducers/slices/auth/lab_report_slice";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import TrfLabConfirmModal from "../TrfLabConfirmModal/index";
import HighRisk from "@assets/images/highrisk.svg";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { GrFormFilter } from "react-icons/gr";
import { useForm } from "react-hook-form";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import LabExemptionModal from "../LabExemptionModal";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";

const ROWS_PER_PAGE = 50;
const STICKY_SHOW_AT = 24;

type LabReportTableScrollMode = "page" | "table";

interface LabReportTableProps {
  scrollMode?: LabReportTableScrollMode;
  source?: boolean
}

interface ILabReportTableFilterForm {
  petDetailsRisk: string;
  testIdSlaStatus: string;
  testNameFilter: string;
}

const PET_DETAILS_FILTER_OPTIONS = [
  { id: "0", title: "All" },
  { id: "1", title: "High Risk" },
];

const TEST_NAME_FILTER_OPTIONS = [
  { id: "0", title: "All" },
  { id: "1", title: "Retest" },
];

const TEST_ID_SLA_FILTER_OPTIONS = [
  { id: "0", title: "All" },
  { id: "1", title: "Delayed" },
  { id: "2", title: "Pickup Delayed" },
  { id: "3", title: "Potential Delayed" },
];

const LabReportTableComponent = ({ scrollMode = "table", source }: LabReportTableProps) => {
  const { getRouteKey } = useRouter();
  const { veticDateFormat, getLabStatusClasses, formatStatus } = useUtils();
  const isCartInvoiceLoading = useLoader("isCustomerInvoiceTableInProgress");
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [visibleRows, setVisibleRows] = useState<any[]>([]);
  const [requisitionId, setRequisitionId] = useState(null);
  const [uploadModalType, setUploadModalType] = useState(null);
  const [invoiceId, setInvoiceId] = useState(null);
  const [uploadReport, setUploadReport] = useState(false);
  const [esCalateReport, setEsCalateReport] = useState(false);
  const [editLabPartner, setEditLabPartner] = useState(false);
  const [testId, setTestId] = useState<string | null>(null);
  const [trfGenerate, setTrfGenerate] = useState(false);
  const [isSticky, setIsSticky] = useState(true);
  const isStickyRef = useRef(true);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isLabTestUrl = location.pathname.includes("lab-test") || location.pathname.includes("closing");
  const normalizedPathname = location?.pathname?.replace(/\/+$/, "") || "";
  const isInternalTab =
    normalizedPathname === "/app/lab-report/internal" || normalizedPathname.includes("lab-test");
  const isInternalRequestsTab = normalizedPathname === "/app/lab-report/internal";
  const isExternalRequestsTab = normalizedPathname === "/app/lab-report/external";
  const isDeleteTRFAllowedTab = isInternalRequestsTab || isExternalRequestsTab;
  const { labReportData, labReportListFilter } = useRouteInfo(getRouteKey("LAB_REPORT", "id")).routeState.state as ILabReportListState;
  const { routeState: customerRouteState } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"));
  const { customerId } = customerRouteState.state as ICustomerProfileState;
  const userPermissions = useAppSelector((state) => state?.auth?.userPermissions);
  const userAllowedActions = userPermissions?.role_permission?.actions || [];
  // RBAC: LAB_REPORT_TAT_VIEW — legacy role: service_desk (also honored via existing "SERVICE_DESK" action)
  const isServiceDeskTATView =
    useFeatureAccess("LAB_REPORT_TAT_VIEW") || userAllowedActions.includes("SERVICE_DESK");
  // RBAC: LAB_REPORT_CLINIC_VIEW — legacy roles: clinic_ops, clinic_ops_doggyworld, clinic_ops_bluecity
  // const isClinicOpsUser = useFeatureAccess("LAB_REPORT_CLINIC_VIEW");
  const isClinicOpsUser =
  ["clinic_ops", "clinic_ops_doggyworld", "clinic_ops_bluecity"]
    .includes(userPermissions?.role_name?.toLowerCase?.() || "");
  // RBAC: LAB_REPORT_RETEST — legacy roles: super_admin, Doctor
  const canRetest = useFeatureAccess("LAB_REPORT_RETEST");
  const [actionType, setActionType] = useState<string | null>(null);
  const [testDetails, setTestDetails] = useState(null);
  const [markExceptiom, setMarkExceptiom] = useState(false)

  const fetchStatsList = useLabReportStatsListAPI();
  const { execute: fetchLabReports } = useLabReportFilterListAPI();
  const { execute: triggerRetest } = useRetestReportAPI();
  const { execute: triggerDeleteTRF } = useDeleteTRFAPI();
  const { execute: fetchRetestReasons, retestReasons } = useRetestReasonsAPI();
  const { execute: fetchDeleteTRFReasons, deleteTRFReasons } = useDeleteTRFReasonsAPI();
  const [retestingRequisitionId, setRetestingRequisitionId] = useState<string | null>(null);
  const [deletingTRFRequisitionId, setDeletingTRFRequisitionId] = useState<string | null>(null);
  const [trfDeleteCandidate, setTrfDeleteCandidate] = useState<any | null>(null);
  const [retestCandidate, setRetestCandidate] = useState<any | null>(null);
  const [retestReason, setRetestReason] = useState<string>("");
  const [trfDeleteReason, setTrfDeleteReason] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const filterRef = useRef(labReportListFilter);
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [trfConfirm, setTrfConfirm] = useState(false);
  const formHook = useForm<ILabReportTableFilterForm>({
    mode: "onChange",
    defaultValues: {
      petDetailsRisk: "0",
      testIdSlaStatus: "0",
      testNameFilter: "0",
    },
  });
  const petDetailsRisk = formHook.watch("petDetailsRisk");
  const testIdSlaStatus = formHook.watch("testIdSlaStatus");
  const testNameFilter = formHook.watch("testNameFilter");
  const reasonFormHook = useForm();
  const retestReasonOptions = useMemo(
    () => retestReasons.map((reason) => ({ id: reason, title: reason })),
    [retestReasons],
  );
  const trfDeleteReasonOptions = useMemo(
    () => deleteTRFReasons.map((reason) => ({ id: reason, title: reason })),
    [deleteTRFReasons],
  );
  const selectedRetestReason = useMemo(
    () => (retestReason ? { id: retestReason, title: retestReason } : undefined),
    [retestReason],
  );
  const selectedTRFDeleteReason = useMemo(
    () => (trfDeleteReason ? { id: trfDeleteReason, title: trfDeleteReason } : undefined),
    [trfDeleteReason],
  );
  const { NO_APPOINTMENTS } = useImageConstants();

  // ── Refs for scroll tracking ──────────────────────────────────────────────
  const lastScrollRef = useRef(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    filterRef.current = labReportListFilter;
  }, [labReportListFilter]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const scrollTop = el.scrollTop;
    setIsSticky(scrollTop <= STICKY_SHOW_AT);
    lastScrollRef.current = scrollTop;
  }, [labReportData?.length]);

  useEffect(() => {
    isStickyRef.current = isSticky;
  }, [isSticky]);

  const getReportType = () => {
    if (location?.pathname?.includes("internal")) return 3;
    if (location?.pathname?.includes("incoming")) return 2;
    return 1;
  };

  const tableHeader = [
    { item: "Client Details" },
    { item: "Pet Details" },
    { item: "Test Name" },
    { item: "Test ID" },
    { item: "Test Date" },
    { item: isServiceDeskTATView ? "Consumer TAT" : "Upload deadline" },
    { item: "Status" },
    { item: "Lab Partner" },
    { item: "Doctor" },
    { item: "Clinic" },
    { item: "Invoice ID" },
    { item: "TRF ID" },
    ...(isInternalTab ? [{ item: "Barcode" }] : []),

    { item: "Action" },
  ];

  const filteredLabReportData = useMemo(() => {
    const sourceData = labReportData ?? [];
    const query = searchQuery.trim().toLowerCase();

    return sourceData.filter((data: any) => {
      const isHighRiskPet = Boolean(data?.high_risk_tag ?? data?.pet_details?.high_risk_tag);
      const matchesPetFilter = petDetailsRisk === "1" ? isHighRiskPet : true;
      const normalizedSlaStatus = String(data?.sla_status ?? "").toLowerCase();

      if (!matchesPetFilter) return false;
      if (testNameFilter === "1" && !data?.parent_requisition_id) return false;
      if (testIdSlaStatus === "1" && normalizedSlaStatus !== "delayed" && normalizedSlaStatus !== "report_delayed") return false;
      if (testIdSlaStatus === "2" && normalizedSlaStatus !== "pickup_delayed") return false;
      if (testIdSlaStatus === "3" && normalizedSlaStatus !== "potential_delay") return false;
      if (!query) return true;

      return (
        data?.lab_partner_name?.toLowerCase().includes(query) ||
        data?.pet_details?.name?.toLowerCase().includes(query) ||
        data?.customer_details?.name?.toLowerCase().includes(query) ||
        data?.test_name?.toLowerCase().includes(query) ||
        data?.requisition_number?.toLowerCase().includes(query) ||
        data?.invoice_number?.toLowerCase().includes(query) ||
        data?.barcodes?.some((barcode: string) => barcode?.toLowerCase().includes(query))
      );
    });
  }, [labReportData, petDetailsRisk, searchQuery, testIdSlaStatus, testNameFilter]);

  useEffect(() => {
    if (filteredLabReportData.length > 0) {
      setVisibleRows(filteredLabReportData.slice(0, ROWS_PER_PAGE));
      setCurrentPage(1);
      setHasMore(filteredLabReportData.length > ROWS_PER_PAGE);
      setIsLoadingMore(false);
    } else {
      setVisibleRows([]);
      setCurrentPage(1);
      setHasMore(false);
      setIsLoadingMore(false);
    }
  }, [filteredLabReportData]);

  const loadMoreRows = useCallback(() => {
    if (!isLoadingMore && hasMore && filteredLabReportData.length > 0) {
      setIsLoadingMore(true);
      const nextPage = currentPage + 1;
      const startIndex = (nextPage - 1) * ROWS_PER_PAGE;
      const endIndex = startIndex + ROWS_PER_PAGE;
      const newRows = filteredLabReportData.slice(startIndex, endIndex);
      if (newRows.length > 0) {
        setVisibleRows((prev) => [...prev, ...newRows]);
        setCurrentPage(nextPage);
        setHasMore(endIndex < filteredLabReportData.length);
      } else {
        setHasMore(false);
      }
      setIsLoadingMore(false);
    }
  }, [currentPage, filteredLabReportData, isLoadingMore, hasMore]);

  // ── Single scroll handler on the outer page div ───────────────────────────
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
      const lastScrollTop = lastScrollRef.current;
      const scrollingDown = scrollTop > lastScrollTop + 4;
      const scrollingUp = scrollTop < lastScrollTop - 4;
      let nextIsSticky = isStickyRef.current;

      if (scrollTop <= STICKY_SHOW_AT) {
        nextIsSticky = true;
      } else if (scrollingDown) {
        nextIsSticky = false; // scrolling down → move with content
      } else if (scrollingUp) {
        nextIsSticky = true; // scrolling up → stick to top
      }

      if (nextIsSticky !== isStickyRef.current) {
        setIsSticky(nextIsSticky);
      }
      lastScrollRef.current = scrollTop;

      // Infinite scroll trigger near the bottom
      if (scrollHeight - scrollTop <= clientHeight + 100 && !isLoadingMore && hasMore) {
        loadMoreRows();
      }
    },
    [loadMoreRows, isLoadingMore, hasMore]
  );

  const getUserProfileUrl = (userId: string, petId: string) => {
    return `/app/customer/profile/customer-details/lab-test/${userId}/${petId}`;
  };

  const isIncomingTab = location.pathname === "/app/lab-report/incoming";

  const getPrimaryAction = (data: any) =>
    isIncomingTab ? data?.external_primary_action_state : data?.primary_action_state;

  const getSecondaryAction = (data: any) =>
    isIncomingTab ? data?.external_secondary_action_state : data?.secondary_action_state;

  const PRIMARY_LABELS: Record<string, string> = {
    trf: "Generate TRF",
    view_report: "View Report",
    handover: "Handover",
    upload_report: "Upload Report",
    pending_for_pickup: "Pending for Pickup",
    picked: "Picked",
  };

  const refreshLabReports = () => {
    const payload: any = {
      start_date: labReportListFilter?.start_date,
      end_date: labReportListFilter?.end_date,
      status_ids: labReportListFilter?.status_ids,
      clinic_ids: labReportListFilter?.clinic_ids,
      type: getReportType(),
    };
    if (labReportListFilter?.lab_partner_ids) {
      payload.lab_partner_ids = labReportListFilter?.lab_partner_ids;
    }
    fetchStatsList(payload as any);
    fetchLabReports(payload);
  };

  const openDeleteTRFModal = (data: any) => {
    if (!data?.requisition_id || deletingTRFRequisitionId) return;
    setTrfDeleteReason("");
    fetchDeleteTRFReasons();
    setTrfDeleteCandidate(data);
  };

  const confirmDeleteTRF = async () => {
    const data = trfDeleteCandidate;
    const reqId = data?.requisition_id;
    if (!reqId) return;
    const reason = trfDeleteReason.trim();
    if (!reason) {
      toast.error("Please select a reason for deleting the TRF");
      return;
    }
    setDeletingTRFRequisitionId(reqId);
    const result = await triggerDeleteTRF(reqId, reason);
    setDeletingTRFRequisitionId(null);
    setTrfDeleteCandidate(null);
    setTrfDeleteReason("");
    if (result?.status) {
      toast.success(result?.message || "TRF deleted");
      refreshLabReports();
    } else {
      toast.error(result?.message || "Could not delete TRF");
    }
  };

  const openRetestModal = (data: any) => {
    if (!data?.requisition_id || retestingRequisitionId) return;
    setRetestReason("");
    fetchRetestReasons();
    setRetestCandidate(data);
  };

  const confirmRetest = async () => {
    const data = retestCandidate;
    const parentRequisitionId = data?.requisition_id;
    if (!parentRequisitionId) return;
    const reason = retestReason.trim();
    if (!reason) {
      toast.error("Please enter a reason for the retest");
      return;
    }
    setRetestingRequisitionId(parentRequisitionId);
    const result = await triggerRetest(parentRequisitionId, reason);
    setRetestingRequisitionId(null);
    setRetestCandidate(null);
    setRetestReason("");
    if (result?.status) {
      toast.success(result?.message || "Retest requisition created");
      refreshLabReports();
    } else {
      toast.error(result?.message || "Could not create retest");
    }
  };

  const SECONDARY_LABELS: Record<string, string> = {
    edit_trf: "Delete TRF",
    reject: "Reject",
    re_upload_report: "Re-upload Report",
  };

  return (
    <div
      ref={scrollMode === "page" ? scrollContainerRef : undefined}
      className={scrollMode === "page" ? "h-screen overflow-y-auto" : ""}
      onScroll={scrollMode === "page" ? handleScroll : undefined}
    >
      {/* ── Sticky header: hides on scroll-down, reappears on scroll-up ── */}
      {!isLabTestUrl && (
        <div
          className={`
            bg-white rounded-lg p-3
            ${isSticky ? "relative md:sticky md:top-0 md:z-20 md:shadow-sm" : "relative"}
          `}
        >
          <h1 className="font-semibold text-base text-[#000000] my-2">Lab Reports Management</h1>
          <div className="mt-2">
            <LabReportHeader resetSelectedStatus={() => setSelectedStatus("")} />
            <LabReportStats selectedStatus={selectedStatus} setSelectedStatus={setSelectedStatus} />
          </div>
          <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-3 md:gap-4 mt-2">
            {!isLabTestUrl && (
              <div className="relative w-full md:w-auto md:min-w-[300px] md:max-w-sm md:shrink-0 bg-white">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xl text-gray-500 pointer-events-none">
                  <Search className="text-gray-400 w-4 h-4" />
                </span>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search Lab Partner, Pet, Client, Test Name, ID & Barcode"
                  className="w-full pl-10 pr-2 py-2 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-gray-100 outline-none transition-all text-sm"
                />
              </div>
            )}
            <div className="w-full md:w-auto md:ml-auto">
              <LabReportFilter resetSelectedStatus={() => setSelectedStatus("")} />
            </div>
          </div>
        </div>
      )}

      <div className=" bg-white rounded-lg mt-2">

        {isCartInvoiceLoading ? (
          <TableSkeletonComponent columnLength={10} bodyLength={8} />
        ) : (
          <div
            ref={scrollMode === "table" ? scrollContainerRef : undefined}
            className={
              scrollMode === "table"
                ? "scroll-inner mt-3 rounded-2xl max-h-[65vh] md:max-h-[calc(100vh-260px)] overscroll-y-contain overflow-y-auto"
                : "scroll-inner mt-3 rounded-2xl"
            }
            onScroll={scrollMode === "table" ? handleScroll : undefined}
          >
            <table className="w-full min-w-[1600px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
              <thead className="bg-[#F5F5F5] text-[#1F1F23] text-xs font-normal sticky top-0 z-10">
                <tr className="py-6">
                  {tableHeader.map((key, index) => (
                    <th
                      key={index}
                      scope="col"
                      className={`p-2 py-3 ${key.item !== "Expected/Uploaded Date" ? "whitespace-nowrap" : ""} text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider sm:sticky top-0 ${key.item === "Action" ? "sm:sticky right-0 bg-gray-100 " : ""}`}
                    >
                      {key.item === "Pet Details" ? (
                        <SearchableSelectMenu
                          onSelectItem={(item) => {
                            if (item) {
                              formHook.setValue("petDetailsRisk", item.id);
                            }
                          }}
                          asButton
                          renderAsButton={
                            <button type="button" className="flex items-center gap-1 text-xs">
                              Pet Details
                              <GrFormFilter className="h-4 w-4" />
                            </button>
                          }
                          register={formHook.register("petDetailsRisk")}
                          selectItems={PET_DETAILS_FILTER_OPTIONS}
                          fieldError={formHook.formState.errors.petDetailsRisk}
                          errorMessages={[]}
                        />
                      ) : key.item === "Test Name" ? (
                        <SearchableSelectMenu
                          onSelectItem={(item) => {
                            formHook.setValue("testNameFilter", item?.id ?? "0");
                          }}
                          asButton
                          renderAsButton={
                            <button type="button" className="flex items-center gap-1 text-xs">
                              Test Name
                              <GrFormFilter className="h-4 w-4" />
                            </button>
                          }
                          register={formHook.register("testNameFilter")}
                          selectItems={TEST_NAME_FILTER_OPTIONS}
                          fieldError={formHook.formState.errors.testNameFilter}
                          errorMessages={[]}
                        />
                      ) : key.item === "Test ID" ? (
                        <SearchableSelectMenu
                          onSelectItem={(item) => {
                            formHook.setValue("testIdSlaStatus", item?.id ?? "0");
                          }}
                          asButton
                          renderAsButton={
                            <button type="button" className="flex items-center gap-1 text-xs">
                              Test ID
                              <GrFormFilter className="h-4 w-4" />
                            </button>
                          }
                          register={formHook.register("testIdSlaStatus")}
                          selectItems={TEST_ID_SLA_FILTER_OPTIONS}
                          fieldError={formHook.formState.errors.testIdSlaStatus}
                          errorMessages={[]}
                        />
                      ) : (
                        key.item
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-xs">
                {visibleRows?.length === 0 && (
                  <tr className="bg-white">
                    <td colSpan={tableHeader.length} className="min-h-[400px] sm:min-h-[500px] md:min-h-[600px]">
                      <div className="flex flex-col items-center justify-center h-full gap-4 py-8">
                        <img
                          src={NO_APPOINTMENTS}
                          alt="No appointments"
                          className="w-32 h-24 sm:w-48 sm:h-32 md:w-64 md:h-44 object-contain"
                        />
                        <p className="text-sm sm:text-base font-medium text-[#6C727F]">No data available</p>
                      </div>
                    </td>
                  </tr>
                )}
                {visibleRows?.map((data, id) => {
                  const isFirst = id === 0;
                  const isLast = id === visibleRows.length - 1;
                  const isSingle = visibleRows.length === 1;
                  const isHighRiskPet = Boolean(data?.high_risk_tag ?? data?.pet_details?.high_risk_tag);

                  return (
                    <tr
                      key={id}
                      className={`
                        bg-white 
                        ${isSingle ? "rounded-xl" : ""}
                        ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                        ${isLast && !isSingle ? "rounded-b-xl" : ""}
                      `}
                    >
                      <td
                        className={`
                          p-2
                          ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                          ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                          ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                        `}
                      >
                        <a href={getUserProfileUrl(data?.customer_details?.user_id, data?.pet_details?.pet_id)} target="_blank" rel="noopener noreferrer" className="text-[#0B6EFF] font-medium items-center flex gap-1 cursor-pointer whitespace-nowrap">
                          <p className="text-xs">{data?.customer_details?.name}</p>
                        </a>
                        {/* <div
                          className="text-[#0B6EFF] font-medium items-center flex gap-1 cursor-pointer whitespace-nowrap"
                          onClick={() =>
                            redirectToUserProfile(data.customer_details.user_id, data.pet_details.pet_id)
                          }
                        >
                          <p className="text-xs">Client Profile</p>
                          <HiOutlineExternalLink />
                        </div> */}
                      </td>

                      <td className="p-2 text-xs font-normal text-black">
                        <div className="flex items-center gap-2">
                          <p className="text-xs">{data?.pet_details?.name || "-"}</p>
                          {isHighRiskPet && (
                            <span className="flex items-center justify-center gap-1 px-[4px] py-[2px] rounded-[5px] bg-[#FFEAEA] text-[#EF0303] text-[0.6875rem] font-semibold leading-none whitespace-nowrap">
                              <img src={HighRisk} alt="High Risk" className="w-3 h-3" />
                              High Risk
                            </span>
                          )}
                        </div>
                        <p className="text-[#6C727F] text-xs">{data?.pet_details?.pet_species_name || "-"}</p>
                        <div className="w-[100px]"><p className="text-[#6C727F] text-xs break-words ">{data?.pet_details?.pet_breed_name || "-"}</p></div>

                      </td>

                      <td className="p-2 text-xs font-normal text-black break-words w-full">
                        <div className="flex flex-col items-start gap-2 w-[200px]">
                          <p className="text-xs">{data?.test_name || "-"}</p>
                          {data?.parent_requisition_id && (
                            <span className="bg-[#FFEDED] text-[#ED0101] px-3 py-1 rounded-full text-xs font-medium ring-1 ring-inset ring-opacity-10 ring-[#ED0101]">
                              Retest
                            </span>
                          )}
                          {data?.child_requisition_id && (
                            <span className="bg-[#FFEDED] text-[#ED0101] px-3 py-1 rounded-full text-xs font-medium ring-1 ring-inset ring-opacity-10 ring-[#ED0101]">
                              Marked Retest
                            </span>
                          )}
                        </div>
                      </td>



                      <td className="p-2 whitespace-nowrap text-xs lg:text-xs font-normal text-black">
                        <div className="flex flex-col items-center gap-2">
                          <p>{data?.requisition_number || "-"}</p>
                          {data?.sla_status && (
                            <span className="bg-[#FFEDED] text-[#ED0101] px-3 py-1 rounded-full text-xs font-medium ring-1 ring-inset ring-opacity-10 ring-[#ED0101]">
                              {data.sla_status
                                .replaceAll("_", " ")
                                .replace(/\b\w/g, (char) => char.toUpperCase())}
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="p-2 whitespace-nowrap text-xs lg:text-xs font-normal text-black">
                        <p>{veticDateFormat(data?.requisition_date) || "-"}</p>
                      </td>

                      <td className={`p-2 whitespace-nowrap text-xs lg:text-xs font-normal text-black`}>
                        {data.requisition_status === "report_uploaded" ? (
                          <p>{veticDateFormat(data?.expected_doc_upload_date) || "NA"}</p>
                        ) : (
                          <p>{veticDateFormat(isServiceDeskTATView ? data?.consumer_tat_date : data?.tat_date) || "NA"}</p>
                        )}
                      </td>

                      <td className="p-2 whitespace-nowrap text-xs font-normal">
                        {data?.requisition_status ? (
                          <p className={`text-xs py-2 rounded-full w-[150px] text-center font-medium ring-1 ring-inset ring-opacity-10
                                         ${getLabStatusClasses(data?.requisition_status)}`}>
                            {formatStatus(data?.requisition_status)}
                          </p>
                        ) : (
                          <span className="block w-[160px]" />
                        )}
                      </td>
                      <td className="p-2 whitespace-nowrap text-xs lg:text-xs font-normal text-black">
                        <div className="flex items-center justify-between gap-2">
                          {data?.is_edit_lab && (
                            <button
                              className="flex-shrink-0"
                              onClick={() => {
                                setEditLabPartner(true);
                                setRequisitionId(data?.requisition_id);
                                setTestId(data?.test_id);
                                setTestDetails(data);
                                setUploadModalType("EDIT");
                              }}
                            >
                              <FiEdit className="w-4 h-4 text-gray-600" />
                            </button>
                          )}
                          <p className="max-w-40 break-words whitespace-nowrap mr-2 flex-1 truncate">
                            {data?.lab_partner_name || "-"}
                          </p>

                        </div>
                      </td>

                      <td className="p-2 whitespace-nowrap text-xs lg:text-xs font-normal text-black">
                        <p>{data?.staff_details?.name || "-"}</p>
                      </td>
                      <td className="p-2 whitespace-nowrap text-xs lg:text-xs font-normal text-black">
                        <p>{data?.clinic_details?.name}</p>
                      </td>
                      <td className="p-2 whitespace-nowrap text-xs lg:text-xs font-normal text-black">
                        <p>{data?.invoice_number || "-"}</p>
                      </td>

                      <td className="p-2 whitespace-nowrap text-xs lg:text-xs font-normal">
                        {data?.trf_code ? (
                          <a
                            href={data?.trf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#0467ff] font-medium hover:underline"
                          >
                            {data.trf_code}
                          </a>
                        ) : (
                          <span className="text-gray-900">-</span>
                        )}
                      </td>

                      {isInternalTab && (
                        <td className="p-2 whitespace-nowrap text-xs lg:text-xs font-normal text-black">
                          {data?.barcodes?.length ? (
                            <div className="flex flex-col gap-1">
                              {data?.barcodes?.map((barcode:any) => (
                                <p>{barcode}</p>
                              ))}
                            </div>
                          ) : (
                            <p>-</p>
                          )}
                        </td>
                      )}

                      <td className="p-2 text-xs sm:sticky right-0 bg-gray-50 z-[5] sm:shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.25)]">
                        {(() => {
                          const initialPrimaryAction = getPrimaryAction(data);
                          const initialSecondaryAction = getSecondaryAction(data);
                          const isExternalRequisition = data?.requisition_type?.toLowerCase?.() === "external";
                          const shouldHideUploadAction = isClinicOpsUser && isExternalRequisition;

                          const primaryAction =
                            shouldHideUploadAction && initialPrimaryAction === "upload_report" ? null : initialPrimaryAction;
                          const hideRejectOnExternalTab = isExternalRequestsTab && initialSecondaryAction === "reject";
                          const secondaryAction =
                            (shouldHideUploadAction && initialSecondaryAction === "re_upload_report") || hideRejectOnExternalTab
                              ? null
                              : initialSecondaryAction;
                          const canDeleteTRF =
                            isDeleteTRFAllowedTab &&
                            !!data?.trf_code &&
                            ["trf_generated", "handed_over"].includes(data?.requisition_status);
                          const showDeleteTRFOption = canDeleteTRF && secondaryAction !== "edit_trf";
                          const showRejectOption =
                            isInternalRequestsTab &&
                            secondaryAction !== "reject" &&
                            ["sample_collected", "trf_generated", "handed_over"].includes(data?.requisition_status);
                          const showRetestOption =
                            canRetest &&
                            isInternalRequestsTab &&
                            !!data?.retest_enable &&
                            data?.requisition_status === "report_uploaded" &&
                            !data?.is_retest;

                          if (!primaryAction && !secondaryAction && !showDeleteTRFOption && !showRejectOption && !showRetestOption) return null;

                          return (
                            <div className="flex items-center gap-2 relative">
                              {primaryAction && (
                                <button
                                  className="w-[120px] py-2 rounded-lg bg-black text-white text-xs font-medium hover:opacity-90"
                                  onClick={() => {
                                    setInvoiceId(data.invoice_number);
                                    setRequisitionId(data.requisition_id);
                                    // if (["view_report", "upload_report", "handover"].includes(primaryAction)) {
                                    //   setTestDetails(data);
                                    // }
                                    setTestDetails(data);
                                    setTestId(data?.test_id);
                                    switch (primaryAction) {
                                      // case "trf":
                                      //   setActionType("trf");
                                      //   setTrfGenerate(true);
                                      //   setUploadModalType("VIEW");
                                      //   break;

                                      case "trf":
                                        setActionType("trf");
                                        setTrfConfirm(true);
                                        setUploadModalType("VIEW");
                                        break;
                                      case "view_report":
                                        setActionType("view_report");
                                        setUploadReport(true);
                                        setUploadModalType("VIEW");
                                        break;
                                      case "upload_report":
                                        setActionType("upload_report");
                                        setUploadReport(true);
                                        setUploadModalType("EDIT");
                                        break;
                                      case "handover":
                                        setActionType("handover");
                                        setTestDetails(data);
                                        setOpen(true);
                                        break;
                                      case "pending_for_pickup":
                                        setActionType("pending_for_pickup");
                                        setOpen(true);
                                        break;
                                      case "picked":
                                        setActionType("picked");
                                        break;
                                    }
                                  }}
                                >
                                  {PRIMARY_LABELS[primaryAction]}
                                </button>
                              )}
                              {source && data.requisition_status === "trf_generated" && <button className="text-red-500 border-black border rounded-md  whitespace-nowrap p-2 font-medium"
                                onClick={() => {
                                  setMarkExceptiom(true);
                                  setInvoiceId(data.invoice_number);
                                  setRequisitionId(data.requisition_id);
                                }}
                              >Handover Pending</button>}

                              {(secondaryAction || showDeleteTRFOption || showRejectOption || showRetestOption) && (() => {
                                const isRetestInFlight = retestingRequisitionId === data?.requisition_id;
                                const isDeletingThisTRF = deletingTRFRequisitionId === data?.requisition_id;
                                return (
                                <Menu as="div" className="relative h-8 w-8">
                                  <Menu.Button className="w-full h-full flex items-center justify-center rounded-md bg-[#F1F1F1] hover:bg-gray-200 transition-colors">
                                    <FiMoreVertical className="w-5 h-5 text-gray-600" />
                                  </Menu.Button>
                                  <Transition
                                    as={Fragment}
                                    enter="transition ease-out duration-100"
                                    enterFrom="transform opacity-0 scale-95"
                                    enterTo="transform opacity-100 scale-100"
                                    leave="transition ease-in duration-75"
                                    leaveFrom="transform opacity-100 scale-100"
                                    leaveTo="transform opacity-0 scale-95"
                                  >
                                    <Menu.Items className="absolute right-12 top-1/2 -translate-y-1/2 w-48 origin-top-right bg-white shadow-xl border border-gray-100 rounded-lg z-[30] focus:outline-none">
                                      <div className="py-1">
                                        {secondaryAction && (
                                          <Menu.Item>
                                            {({ active }) => {
                                              const isDeletingThisTRF =
                                                secondaryAction === "edit_trf" &&
                                                deletingTRFRequisitionId === data?.requisition_id;
                                              return (
                                              <button
                                                disabled={isDeletingThisTRF}
                                                className={`w-full text-left px-4 py-2 text-sm transition-colors ${active ? "bg-gray-50 text-black" : "text-gray-700"} ${isDeletingThisTRF ? "opacity-50 cursor-not-allowed" : ""}`}
                                                onClick={() => {
                                                  setInvoiceId(data.invoice_number);
                                                  setRequisitionId(data.requisition_id);
                                                  switch (secondaryAction) {
                                                    case "edit_trf":
                                                      openDeleteTRFModal(data);
                                                      break;
                                                    case "reject":
                                                      setActionType("reject");
                                                      setTestDetails(data);
                                                      setEsCalateReport(true);
                                                      break;
                                                    case "re_upload_report":
                                                      setActionType("re_upload_report");
                                                      setTestDetails(data);
                                                      setUploadReport(true);
                                                      setUploadModalType("EDIT");
                                                      break;
                                                  }
                                                }}
                                              >
                                                {isDeletingThisTRF ? "Deleting…" : SECONDARY_LABELS[secondaryAction]}
                                              </button>
                                              );
                                            }}
                                          </Menu.Item>
                                        )}
                                        {showDeleteTRFOption && (
                                          <Menu.Item>
                                            {({ active }) => (
                                              <button
                                                disabled={isDeletingThisTRF}
                                                className={`w-full text-left px-4 py-2 text-sm transition-colors ${active ? "bg-gray-50 text-black" : "text-gray-700"} ${isDeletingThisTRF ? "opacity-50 cursor-not-allowed" : ""}`}
                                                onClick={() => openDeleteTRFModal(data)}
                                              >
                                                {isDeletingThisTRF ? "Deleting…" : "Delete TRF"}
                                              </button>
                                            )}
                                          </Menu.Item>
                                        )}
                                        {showRejectOption && (
                                          <Menu.Item>
                                            {({ active }) => (
                                              <button
                                                className={`w-full text-left px-4 py-2 text-sm transition-colors ${active ? "bg-gray-50 text-black" : "text-gray-700"}`}
                                                onClick={() => {
                                                  setInvoiceId(data.invoice_number);
                                                  setRequisitionId(data.requisition_id);
                                                  setActionType("reject");
                                                  setTestDetails(data);
                                                  setEsCalateReport(true);
                                                }}
                                              >
                                                Reject
                                              </button>
                                            )}
                                          </Menu.Item>
                                        )}
                                        {showRetestOption && (
                                          <Menu.Item>
                                            {({ active }) => (
                                              <button
                                                disabled={isRetestInFlight}
                                                className={`w-full text-left px-4 py-2 text-sm transition-colors ${active ? "bg-gray-50 text-black" : "text-gray-700"} ${isRetestInFlight ? "opacity-50 cursor-not-allowed" : ""}`}
                                                onClick={() => openRetestModal(data)}
                                              >
                                                {isRetestInFlight ? "Re-testing…" : "Re-test"}
                                              </button>
                                            )}
                                          </Menu.Item>
                                        )}
                                      </div>
                                    </Menu.Items>
                                  </Transition>
                                </Menu>
                                );
                              })()}
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                })}

                {/* ── Modals ── */}
                {retestCandidate && (
                  <Dialog
                    as="div"
                    className="fixed inset-0 z-[60] overflow-y-auto"
                    open={!!retestCandidate}
                    onClose={() => {
                      if (retestingRequisitionId) return;
                      setRetestCandidate(null);
                      setRetestReason("");
                    }}
                  >
                    <div className="flex min-h-screen items-center justify-center bg-black/50 backdrop-blur-sm px-4 py-8">
                      <Dialog.Panel className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-visible">
                        <div className="px-6 py-5 border-b border-gray-100">
                          <Dialog.Title className="text-base font-bold text-gray-900">Re-test</Dialog.Title>
                          <div className="mt-4">
                            <label className="text-[11px] font-bold text-gray-700 tracking-wider flex items-center gap-1.5">
                              Reason for Retest <span className="text-red-500">*</span>
                            </label>
                            <SearchableSelectMenu
                              register={reasonFormHook.register("retestReason")}
                              fieldError={undefined}
                              errorMessages={[]}
                              selectItems={retestReasonOptions}
                              defaultSelected={selectedRetestReason}
                              placeholder={retestReasons.length ? "Select reason" : "Loading reasons..."}
                              onSelectItem={(item) => setRetestReason(item?.id ?? "")}
                              showDropdownIcon
                              containerClassName="mt-1.5 w-full"
                              listZIndex="z-[70]"
                            />
                          </div>
                        </div>
                        <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 rounded-b-2xl">
                          <button
                            type="button"
                            disabled={!!retestingRequisitionId}
                            onClick={() => {
                              setRetestCandidate(null);
                              setRetestReason("");
                            }}
                            className="px-4 py-2 text-xs font-bold text-gray-600 hover:text-gray-900 transition-colors disabled:opacity-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            disabled={!!retestingRequisitionId || !retestReason.trim()}
                            onClick={confirmRetest}
                            className={`rounded-xl px-5 py-2 text-xs font-bold text-white shadow-sm transition-all ${
                              retestingRequisitionId || !retestReason.trim() ? "bg-gray-400 cursor-not-allowed" : "bg-black hover:bg-gray-800"
                            }`}
                          >
                            {retestingRequisitionId ? "Creating…" : "Create Retest"}
                          </button>
                        </div>
                      </Dialog.Panel>
                    </div>
                  </Dialog>
                )}
                {trfDeleteCandidate && (
                  <Dialog
                    as="div"
                    className="fixed inset-0 z-[60] overflow-y-auto"
                    open={!!trfDeleteCandidate}
                    onClose={() => {
                      if (deletingTRFRequisitionId) return;
                      setTrfDeleteCandidate(null);
                      setTrfDeleteReason("");
                    }}
                  >
                    <div className="flex min-h-screen items-center justify-center bg-black/50 backdrop-blur-sm px-4 py-8">
                      <Dialog.Panel className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-visible">
                        <div className="px-6 py-5 border-b border-gray-100">
                          <Dialog.Title className="text-base font-bold text-gray-900">Delete TRF</Dialog.Title>
                          <div className="mt-4">
                            <label className="text-[11px] font-bold text-gray-700 tracking-wider flex items-center gap-1.5">
                              Reason for Deleting TRF <span className="text-red-500">*</span>
                            </label>
                            <SearchableSelectMenu
                              register={reasonFormHook.register("trfDeleteReason")}
                              fieldError={undefined}
                              errorMessages={[]}
                              selectItems={trfDeleteReasonOptions}
                              defaultSelected={selectedTRFDeleteReason}
                              placeholder={deleteTRFReasons.length ? "Select reason" : "Loading reasons..."}
                              onSelectItem={(item) => setTrfDeleteReason(item?.id ?? "")}
                              showDropdownIcon
                              containerClassName="mt-1.5 w-full"
                              listZIndex="z-[70]"
                            />
                          </div>
                        </div>
                        <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 rounded-b-2xl">
                          <button
                            type="button"
                            disabled={!!deletingTRFRequisitionId}
                            onClick={() => {
                              setTrfDeleteCandidate(null);
                              setTrfDeleteReason("");
                            }}
                            className="px-4 py-2 text-xs font-bold text-gray-600 hover:text-gray-900 transition-colors disabled:opacity-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            disabled={!!deletingTRFRequisitionId || !trfDeleteReason.trim()}
                            onClick={confirmDeleteTRF}
                            className={`rounded-xl px-5 py-2 text-xs font-bold text-white shadow-sm transition-all ${
                              deletingTRFRequisitionId || !trfDeleteReason.trim()
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-[#ED0101] hover:bg-red-700"
                            }`}
                          >
                            {deletingTRFRequisitionId ? "Deleting…" : "Delete TRF"}
                          </button>
                        </div>
                      </Dialog.Panel>
                    </div>
                  </Dialog>
                )}
                {markExceptiom && (
                  <LabExemptionModal
                    open={markExceptiom}
                    setOpen={setMarkExceptiom}
                    requisitionId={requisitionId}
                    invoiceId={invoiceId}
                    testDetails={testDetails}
                  />
                )}
                {open && (
                  <SamplePickupRequestModal
                    open={open}
                    setOpen={setOpen}
                    requisitionId={requisitionId}
                    invoiceId={invoiceId}
                    testDetails={testDetails}
                  />
                )}
                {uploadReport && (
                  <UploadLabReportModal
                    open={uploadReport}
                    setOpen={setUploadReport}
                    requisitionId={requisitionId}
                    invoiceId={invoiceId}
                    uploadModalType={uploadModalType}
                    testDetails={testDetails}
                    actionType={actionType}
                  />
                )}
                {trfGenerate && (
                  <TrfGenerateModal
                    open={trfGenerate}
                    setOpen={setTrfGenerate}
                    requisitionId={requisitionId}
                    uploadModalType={uploadModalType}
                    actionType={actionType}
                  />
                )}
                {esCalateReport && (
                  <EscalateReportModal
                    open={esCalateReport}
                    setOpen={setEsCalateReport}
                    requisitionId={requisitionId}
                    invoiceId={invoiceId}
                    testDetails={testDetails}
                  />
                )}
                {editLabPartner && (
                  <EditLabPartnerModal
                    open={editLabPartner}
                    setOpen={setEditLabPartner}
                    uploadModalType={uploadModalType}
                    requisitionId={requisitionId}
                    testId={testId}
                    testDetails={testDetails}
                  />
                )}
                {trfConfirm && (
                  <TrfLabConfirmModal
                    open={trfConfirm}
                    setOpen={setTrfConfirm}
                    testDetails={testDetails}
                    onCancel={() => {
                      setTrfConfirm(false);
                      setTrfGenerate(true);
                    }}
                    onChangeLab={() => {
                      setTrfConfirm(false);
                      setEditLabPartner(true);
                      setUploadModalType("EDIT");
                    }}
                  />
                )}
              </tbody>
            </table>

            {hasMore && (
              <div className="text-center py-4">
                {isLoadingMore ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                    <span className="ml-2">Loading more...</span>
                  </div>
                ) : (
                  <div className="text-gray-500">Scroll to load more</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LabReportTableComponent;
